<template>
  <RunStatusButtonList
    v-model="selectedProbability"
    class="RunStatusButtonView"
    :isDisabled="isDisabled"
    v-bind="$attrs"
  >
    <Teleport to="body">
      <Transition>
        <PositionModalView
          v-if="isUpdatingPosition"
          @close="isUpdatingPosition = false"
        />
      </Transition>
    </Teleport>
  </RunStatusButtonList>
</template>

<script lang="ts" setup>
import { supabaseClient } from "@/lib/database/supabase";
import haptic from "@/lib/utils/platform/haptic";
import useAuthStore from "@/stores/useAuthStore";
import RunStatusButtonList from "@/views/RunStatusButtonList.vue";
import PositionModalView from "@/views/modals/PositionModalView.vue";
import { ImpactStyle } from "@capacitor/haptics";
import { RunSummary, StatusSummary, StatusSummaryWithPlayer } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { computed, ref, toRefs, watch } from "vue";

const props = withDefaults(defineProps<{
  run: RunSummary & {
    statuses: Array<StatusSummaryWithPlayer>,
  },
  currentPlayerStatus?: StatusSummaryWithPlayer | null,
}>(), {
  currentPlayerStatus: null,
});

const {
  run,
  currentPlayerStatus,
} = toRefs(props);

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const isUpdatingPosition = ref(false);

const isDisabled = computed(() => {
  return !!run.value.canceled_at;
});

const selectedProbability = computed<number | null>({
  get () {
    return currentPlayerStatus.value?.probability ?? null;
  },
  set (value) {
    haptic(ImpactStyle.Heavy);

    if (value === null) {
      if (currentPlayerStatus.value) handleRemoveStatus(currentPlayerStatus.value);
      return;
    }

    handleUpdateStatus(value);
  },
});

// TODO: DRY
function removeStatus (status: Partial<StatusSummary>) {
  if (!run.value) return;
  const index = run.value.statuses.findIndex((_status) => _status.run_id === status.run_id && _status.player_id === status.player_id);

  if (index !== -1) run.value.statuses.splice(index, 1);
}

async function handleUpdateStatus (probability: number) {
  if (!run.value || !currentPlayer.value) return;

  try {
    if (!currentPlayer.value.position) {
      isUpdatingPosition.value = true;

      await new Promise((resolve, reject) => {
        watch(() => currentPlayer.value?.position, (_posiiton) => {
          if (_posiiton) resolve(_posiiton);
        });

        watch(isUpdatingPosition, () => {
          if (!isUpdatingPosition.value && !currentPlayer.value?.position) reject();
        });
      });
    }
  } catch (_err) {
    return;
  }

  const {
    status,
    error,
  } = await upsertStatus(probability);

  if (error) {
    alert(error.message);
  } else {
    const existingStatus = run.value.statuses.find((_status) => _status.player_id === currentPlayer.value?.id);
    if (existingStatus) {
      Object.assign(existingStatus, status);
    } else if (status) {
      run.value.statuses.push({
        ...status,
        player: currentPlayer.value,
      });
    }
  }
}

async function handleRemoveStatus (_status: StatusSummary) {
  const {
    data:status,
    error,
  } = await supabaseClient
    .from("statuses")
    .delete()
    .eq("run_id", _status.run_id)
    .eq("player_id", _status.player_id)
    .select("*")
    .maybeSingle();

  if (error) {
    alert(error.message);
  } else if (status) {
    removeStatus(status);
  }
}

async function upsertStatus (probability: number) {
  if (!run.value || !currentPlayer.value) return {
    status: null,
    error: new Error("Run or current player are missing"),
  };

  if (currentPlayerStatus.value) {
    const {
      data: status,
      error,
    } = await supabaseClient.from("statuses")
      .update({
        probability,
      })
      .eq("run_id", run.value.id)
      .eq("player_id", currentPlayer.value.id)
      .select()
      .maybeSingle();

    return {
      status,
      error,
    };
  }

  const {
    data: status,
    error,
  } = await supabaseClient.from("statuses")
    .upsert({
      run_id: run.value.id,
      player_id: currentPlayer.value.id,
      probability,
    })
    .select()
    .maybeSingle();

  return {
    status,
    error,
  };
}
</script>
